import React from 'react';
import { useFitnessWizard } from '../../contexts/FitnessWizardContext';
import { ThemedButton } from '../ThemedButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Users } from 'lucide-react';

const FitnessMembership = () => {
  const { formData, updateFormData, nextStep } = useFitnessWizard();

  const handleSubmit = (e) => {
    e.preventDefault();
    nextStep();
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="bg-[#232323] text-white rounded-t-xl">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6" />
          <div>
            <CardTitle className="text-xl">Membership & Participation Volume</CardTitle>
            <CardDescription className="text-white/60">
              Help us understand your studio's scale
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="active_members">Estimated Active Members *</Label>
            <p className="text-sm text-slate-600 mb-2">Total number of members with active memberships</p>
            <Input
              id="active_members"
              type="number"
              data-testid="active-members-input"
              value={formData.active_members}
              onChange={(e) => updateFormData({ active_members: parseInt(e.target.value) || '' })}
              required
              className="mt-1.5"
              placeholder="e.g., 250"
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="average_daily_checkins">Average Daily Check-ins *</Label>
            <p className="text-sm text-slate-600 mb-2">Typical number of visitors per day</p>
            <Input
              id="average_daily_checkins"
              type="number"
              data-testid="daily-checkins-input"
              value={formData.average_daily_checkins}
              onChange={(e) => updateFormData({ average_daily_checkins: parseInt(e.target.value) || '' })}
              required
              className="mt-1.5"
              placeholder="e.g., 50"
              min="0"
            />
          </div>

          <div>
            <Label htmlFor="num_instructors">Number of Instructors / Trainers / Coaches *</Label>
            <p className="text-sm text-slate-600 mb-2">Include both full-time and part-time staff</p>
            <Input
              id="num_instructors"
              type="number"
              data-testid="num-instructors-input"
              value={formData.num_instructors}
              onChange={(e) => updateFormData({ num_instructors: parseInt(e.target.value) || '' })}
              required
              className="mt-1.5"
              placeholder="e.g., 8"
              min="0"
            />
          </div>

          {/* Youth Participants */}
          <div className="p-4 border border-slate-200 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="has_youth">Do you have any youth participants? (Under 18)</Label>
                <p className="text-sm text-slate-600">This affects underwriting considerations</p>
              </div>
              <Switch
                id="has_youth"
                data-testid="has-youth-switch"
                checked={formData.has_youth_participants}
                onCheckedChange={(checked) => {
                  updateFormData({ 
                    has_youth_participants: checked,
                    youth_participant_count: checked ? formData.youth_participant_count : ''
                  });
                }}
              />
            </div>

            {formData.has_youth_participants && (
              <div>
                <Label htmlFor="youth_participant_count">Estimated Youth Participant Count</Label>
                <Input
                  id="youth_participant_count"
                  type="number"
                  data-testid="youth-count-input"
                  value={formData.youth_participant_count}
                  onChange={(e) => updateFormData({ youth_participant_count: parseInt(e.target.value) || '' })}
                  className="mt-1.5"
                  placeholder="e.g., 30"
                  min="0"
                />
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="bg-[#EEF0FF] border border-[#7B6FE8]/20 rounded-lg p-4">
            <h4 className="font-medium text-[#1A1A1A] mb-2">Membership Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-600">Active Members:</span>
                <span className="ml-2 font-medium text-slate-900">{formData.active_members || '—'}</span>
              </div>
              <div>
                <span className="text-slate-600">Daily Check-ins:</span>
                <span className="ml-2 font-medium text-slate-900">{formData.average_daily_checkins || '—'}</span>
              </div>
              <div>
                <span className="text-slate-600">Staff:</span>
                <span className="ml-2 font-medium text-slate-900">{formData.num_instructors || '—'}</span>
              </div>
              <div>
                <span className="text-slate-600">Youth:</span>
                <span className="ml-2 font-medium text-slate-900">
                  {formData.has_youth_participants ? (formData.youth_participant_count || 'Yes') : 'No'}
                </span>
              </div>
            </div>
          </div>

          <ThemedButton 
            type="submit"
            data-testid="fitness-membership-next-btn"
            disabled={!formData.active_members || !formData.average_daily_checkins || !formData.num_instructors}
            variant="wizard" size="full"
          >
            Next: Coverage Options
          </ThemedButton>
        </form>
      </CardContent>
    </Card>
  );
};

export default FitnessMembership;
